import { ReactElement, MutableRefObject, useRef, useState, useEffect, useCallback, useMemo, CSSProperties } from 'react';
import { css, keyframes, Theme } from '@emotion/react';

export type CloseAnimationCallback = (props: {
  ref: MutableRefObject<HTMLDivElement | null>;
  isClosing: boolean;
  onAnimateClose: () => void;
}) => ReactElement;

export interface CloseAnimationProps {
  open: boolean;
  onClose?: () => void;
  children: CloseAnimationCallback;
}

export const CloseAnimation = ({ open, children, onClose, ...other }: CloseAnimationProps) => {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState([0, 0]);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    open && setIsClosing(false);
  }, [open]);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    if (!targetRef.current) return;
    if (!onClose) return;

    const bounds = targetRef.current.getBoundingClientRect();
    setSize([bounds.height, bounds.width]);
    setIsClosing(true);

    setTimeout(() => {
      setSize([0, bounds.width]);
    }, 1000);

    setTimeout(() => onClose(), 3000);
  }, [isClosing, onClose]);

  const props = useMemo(
    () => ({
      ref: targetRef,
      isClosing,
      onAnimateClose: handleClose,
    }),
    [targetRef, isClosing, handleClose]
  );

  if (!open) return null;

  const component = children(props);

  if (!isClosing) return component;

  return (
    <>
      <div {...other} style={{ '--height': `${size[0]}px`, '--width': `${size[1]}px` } as CSSProperties} css={closingContainerStyle}>
        <div css={closingAnimationStyle}>{component}</div>
      </div>
    </>
  );
};

const closingContainerAnimation = keyframes`
  0% { max-height: var(--height); margin-bottom: var(--margin, 16px); }
  20% { max-height: var(--height); margin-bottom: var(--margin, 16px); }
  100% { max-height: 0px; margin-bottom: 0px; }
`;

const closingContainerStyle = (theme: Theme) => css`
  position: relative;
  margin-bottom: 0px;
  height: var(--height);

  animation: 1000ms ${closingContainerAnimation} 1 ${theme.easing.basic};
`;

const closingAnimation = keyframes`
  0% { clip-path: circle(200% at calc(100% - 34px) 34px); }
  100% { clip-path: circle(0% at calc(100% - 34px) 34px); }
`;

const closingAnimationStyle = (theme: Theme) => css`
  position: absolute;
  height: var(--height);
  width: var(--width);
  clip-path: circle(0% at calc(100% - 16px) 16px);

  animation: 800ms ${closingAnimation} 1 ${theme.easing.springy};
`;
